<app-page-shell>
  <app-page-hero
    eyebrow="Transport Planning"
    title="Add to Existing Shipment"
    subtitle="Attach ready, unassigned order releases to existing shipments or unstarted deliveries. No changes are saved until final confirmation.">
    <div actions>
      <p-button
        label="Order Releases"
        icon="pi pi-arrow-left"
        severity="secondary"
        [outlined]="true"
        routerLink="/transport-planning/releases"></p-button>
    </div>
  </app-page-hero>

  <!-- Step Progress Indicator -->
  <div class="steps-container">
    <div class="step-item" [class.step-active]="step === 'MATCH'" [class.step-complete]="step === 'REVIEW' || step === 'CONFIRMATION'">
      <div class="step-badge">1</div>
      <div class="step-label">
        <strong>Match Candidates</strong>
        <small>Ranked fit suggestions</small>
      </div>
    </div>
    <div class="step-divider"></div>
    <div class="step-item" [class.step-active]="step === 'REVIEW'" [class.step-complete]="step === 'CONFIRMATION'">
      <div class="step-badge">2</div>
      <div class="step-label">
        <strong>Review Combined Impact</strong>
        <small>Route and financial changes</small>
      </div>
    </div>
    <div class="step-divider"></div>
    <div class="step-item" [class.step-active]="step === 'CONFIRMATION'">
      <div class="step-badge">3</div>
      <div class="step-label">
        <strong>Confirmation</strong>
        <small>Independent outcomes</small>
      </div>
    </div>
  </div>

  @if (errorMessage) {
    <div class="global-notice global-notice--error">
      <i class="pi pi-exclamation-circle"></i>
      <span>{{ errorMessage }}</span>
    </div>
  }
  @if (message) {
    <div class="global-notice global-notice--info">
      <i class="pi pi-info-circle"></i>
      <span>{{ message }}</span>
    </div>
  }

  <!-- ==================== STEP 1: MATCH CANDIDATES ==================== -->
  @if (step === 'MATCH') {
    <app-page-data-panel
      title="Provisional Shipment Matching"
      subtitle="Select a ranked candidate shipment or delivery for each release. Multiple releases can be assigned to the same target for a combined review.">

      <div class="step-header-summary">
        <div>
          <span>{{ releases.length }} selected release{{ releases.length === 1 ? '' : 's' }}</span>
          <span class="bullet-sep">•</span>
          <strong class="highlight-count">{{ assignedReleasesCount() }} assigned to a candidate</strong>
        </div>
        <p-button
          label="Proceed to Review ({{ assignedReleasesCount() }})"
          icon="pi pi-arrow-right"
          iconPos="right"
          [disabled]="!canProceedToReview()"
          (onClick)="proceedToReview()"></p-button>
      </div>

      <div class="release-match-list">
        @for (release of releases; track release.releaseId) {
          <div class="release-card">
            <div class="release-card-head">
              <div class="release-meta-main">
                <span class="release-number">{{ release.releaseNumber }}</span>
                @if (release.sourceNumber || release.customerTrackingNumber) {
                  <span class="meta-tag">Order {{ release.sourceNumber || release.customerTrackingNumber }}</span>
                }
                @if (release.crmClientName || release.companyName || release.customerName) {
                  <span class="meta-tag meta-tag--client">{{ release.crmClientName || release.companyName || release.customerName }}</span>
                }
              </div>
              <div class="release-meta-route">
                <span class="route-point"><i class="pi pi-circle-fill origin-dot"></i> {{ release.originName || ('Location #' + release.originLocationId) }}</span>
                <i class="pi pi-arrow-right route-arrow"></i>
                <span class="route-point"><i class="pi pi-map-marker dest-dot"></i> {{ release.destinationName || ('Location #' + release.destinationLocationId) }}</span>
              </div>
              <div class="release-meta-specs">
                <span>{{ releaseWeightSummary(release) }}</span>
                <span class="bullet-sep">•</span>
                <span>{{ releasePalletSummary(release) }}</span>
                <span class="bullet-sep">•</span>
                <span>Need by {{ formatDate(release.deliveryWindowEnd || release.deliveryWindowStart) }}</span>
              </div>
            </div>

            <!-- Candidate Suggestions -->
            <div class="candidate-section">
              <div class="candidate-section-title">
                <strong>Ranked Shipment Candidates</strong>
                <small>Calculated using operational fit index (detour 45%, schedule 25%, capacity 20%, stop reuse 10%)</small>
              </div>

              @if (matchLoadingByReleaseId.get(release.releaseId!)) {
                <div class="loading-matches">
                  <i class="pi pi-spin pi-spinner"></i>
                  <span>Evaluating candidate shipments and deliveries...</span>
                </div>
              } @else {
                @let matchPage = matchesByReleaseId.get(release.releaseId!);
                @if (!matchPage || !matchPage.items || matchPage.items.length === 0) {
                  <div class="empty-candidates">
                    <i class="pi pi-info-circle"></i>
                    <span>No eligible shipments or unstarted deliveries found for this release.</span>
                  </div>
                } @else {
                  <div class="candidate-grid">
                    <!-- Unassigned Option -->
                    <label class="candidate-card candidate-card--unassigned" [class.candidate-card--selected]="isCandidateSelected(release.releaseId!, null)">
                      <div class="candidate-radio">
                        <p-radioButton
                          [name]="'candidate-' + release.releaseId"
                          [value]="null"
                          [ngModel]="selectedCandidateByReleaseId.get(release.releaseId!)"
                          (ngModelChange)="selectCandidate(release.releaseId!, null)"></p-radioButton>
                      </div>
                      <div class="candidate-content">
                        <strong>Skip / Do Not Assign</strong>
                        <small>Leave this release in the pool for other planning workflows.</small>
                      </div>
                    </label>

                    <!-- Candidate Match Cards -->
                    @for (candidate of matchPage.items; track (candidate.shipmentId + '-' + (candidate.deliveryId || '0'))) {
                      <label
                        class="candidate-card"
                        [class.candidate-card--selected]="isCandidateSelected(release.releaseId!, candidate)"
                        [class.candidate-card--excluded]="candidate.eligible === false">
                        <div class="candidate-radio">
                          <p-radioButton
                            [name]="'candidate-' + release.releaseId"
                            [value]="candidate"
                            [disabled]="candidate.eligible === false"
                            [ngModel]="selectedCandidateByReleaseId.get(release.releaseId!)"
                            (ngModelChange)="selectCandidate(release.releaseId!, candidate)"></p-radioButton>
                        </div>
                        <div class="candidate-content">
                          <div class="candidate-header">
                            <div class="target-title">
                              <strong>Shipment {{ candidate.shipmentNumber }}</strong>
                              @if (candidate.deliveryNumber) {
                                <span class="delivery-badge">Delivery {{ candidate.deliveryNumber }}</span>
                              } @else {
                                <span class="no-delivery-badge">Shipment without delivery</span>
                              }
                            </div>
                            <div class="fit-indicator">
                              <p-tag
                                [value]="candidate.fitPercentage + '% Operational Fit'"
                                [severity]="fitPercentageSeverity(candidate.fitPercentage)"></p-tag>
                            </div>
                          </div>

                          @if (candidate.eligible === false) {
                            <div class="exclusion-banner">
                              <i class="pi pi-ban"></i>
                              <span>Excluded: {{ candidate.exclusionReason || 'Candidate failed eligibility checks' }}</span>
                            </div>
                          } @else {
                            <!-- Score Breakdown Reasons -->
                            <div class="score-reasons">
                              @if (candidate.scoreBreakdown?.detourReason) {
                                <div class="reason-row">
                                  <i class="fa-solid fa-route reason-icon"></i>
                                  <span>{{ candidate.scoreBreakdown?.detourReason }}</span>
                                </div>
                              }
                              @if (candidate.scoreBreakdown?.slackReason) {
                                <div class="reason-row">
                                  <i class="fa-regular fa-clock reason-icon"></i>
                                  <span>{{ candidate.scoreBreakdown?.slackReason }}</span>
                                </div>
                              }
                              @if (candidate.scoreBreakdown?.capacityReason) {
                                <div class="reason-row">
                                  <i class="fa-solid fa-weight-hanging reason-icon"></i>
                                  <span>{{ candidate.scoreBreakdown?.capacityReason }}</span>
                                </div>
                              }
                              @if (candidate.scoreBreakdown?.reuseReason) {
                                <div class="reason-row">
                                  <i class="fa-solid fa-recycle reason-icon"></i>
                                  <span>{{ candidate.scoreBreakdown?.reuseReason }}</span>
                                </div>
                              }
                            </div>

                            <!-- Impacts & Asset Summary -->
                            <div class="candidate-impacts">
                              @if (candidate.estimatedImpacts?.detourDistanceKm !== undefined) {
                                <span class="impact-pill">
                                  <i class="pi pi-arrows-h"></i>
                                  {{ formatDelta(candidate.estimatedImpacts?.detourDistanceKm, 'km detour') }}
                                </span>
                              }
                              @if (candidate.estimatedImpacts?.durationDeltaMinutes !== undefined) {
                                <span class="impact-pill">
                                  <i class="pi pi-clock"></i>
                                  {{ formatMinutes(candidate.estimatedImpacts?.durationDeltaMinutes) }}
                                </span>
                              }
                              @if (candidate.estimatedImpacts?.reusedStopsCount) {
                                <span class="impact-pill impact-pill--reuse">
                                  <i class="pi pi-replay"></i>
                                  Reuses {{ candidate.estimatedImpacts?.reusedStopsCount }} stop{{ candidate.estimatedImpacts?.reusedStopsCount === 1 ? '' : 's' }}
                                </span>
                              }
                              @if (candidate.vehicleTypeName) {
                                <span class="impact-pill">
                                  <i class="fa-solid fa-truck"></i>
                                  {{ candidate.vehicleTypeName }}
                                </span>
                              }
                              @if (candidate.estimatedImpacts?.plateNumber) {
                                <span class="impact-pill">
                                  <i class="fa-solid fa-id-card"></i>
                                  {{ candidate.estimatedImpacts?.plateNumber }}
                                </span>
                              }
                              @if (!candidate.estimatedImpacts?.timingVerified || !candidate.estimatedImpacts?.capacityVerified) {
                                <span class="unverified-note">
                                  <i class="pi pi-question-circle"></i>
                                  Timing/capacity unverified (0% subscore)
                                </span>
                              }
                            </div>
                          }
                        </div>
                      </label>
                    }
                  </div>
                }
              }
            </div>
          </div>
        }
      </div>

      <div class="step-bottom-actions">
        <p-button
          label="Cancel / Return to Releases"
          severity="secondary"
          [outlined]="true"
          routerLink="/transport-planning/releases"></p-button>
        <p-button
          label="Proceed to Review ({{ assignedReleasesCount() }})"
          icon="pi pi-arrow-right"
          iconPos="right"
          [disabled]="!canProceedToReview()"
          (onClick)="proceedToReview()"></p-button>
      </div>
    </app-page-data-panel>
  }

  <!-- ==================== STEP 2: REVIEW COMBINED IMPACT ==================== -->
  @if (step === 'REVIEW') {
    <app-page-data-panel
      title="Combined Route & Impact Review"
      subtitle="Review the consolidated stop sequence, route detour, and financial impact for each target shipment. Confirm each shipment independently.">

      <div class="review-global-info">
        <i class="pi pi-info-circle"></i>
        <span>
          Releases assigned to the same target are evaluated together. Previews remain valid for 30 minutes. Each shipment is confirmed independently.
        </span>
      </div>

      <div class="target-reviews-list">
        @for (target of targetPreviews; track target.targetKey) {
          <div class="target-review-card" [class.target-review-card--confirmed]="target.status === 'CONFIRMED'">
            <!-- Target Card Header -->
            <div class="target-card-head">
              <div class="target-main-info">
                <h3>
                  Shipment {{ target.shipmentNumber }}
                  @if (target.deliveryNumber) {
                    <span class="delivery-badge">Delivery {{ target.deliveryNumber }}</span>
                  } @else {
                    <span class="no-delivery-badge">Shipment without delivery</span>
                  }
                </h3>
                <div class="assigned-releases-strip">
                  <span class="meta-label">Releases to attach:</span>
                  @for (rel of target.assignedReleases; track rel.releaseId) {
                    <span class="release-pill">{{ rel.releaseNumber }}</span>
                  }
                </div>
              </div>

              <div class="target-head-actions">
                @if (target.status === 'CONFIRMED') {
                  <p-tag severity="success" value="Confirmed"></p-tag>
                } @else if (target.status === 'EXPIRED') {
                  <p-tag severity="warn" value="Preview Expired"></p-tag>
                  <p-button
                    label="Refresh Preview"
                    icon="pi pi-refresh"
                    size="small"
                    severity="warn"
                    (onClick)="refreshPreview(target)"></p-button>
                } @else if (target.status === 'LOADING') {
                  <p-tag severity="info" value="Calculating..."></p-tag>
                } @else {
                  <p-button
                    label="Refresh"
                    icon="pi pi-refresh"
                    size="small"
                    severity="secondary"
                    [outlined]="true"
                    (onClick)="refreshPreview(target)"></p-button>
                }
              </div>
            </div>

            <!-- Error Banner -->
            @if (target.errorMessage) {
              <div class="target-error-banner">
                <i class="pi pi-exclamation-triangle"></i>
                <span>{{ target.errorMessage }}</span>
                <p-button
                  label="Retry"
                  icon="pi pi-refresh"
                  size="small"
                  severity="danger"
                  (onClick)="refreshPreview(target)"></p-button>
              </div>
            }

            @if (target.status === 'LOADING') {
              <div class="target-loading">
                <i class="pi pi-spin pi-spinner"></i>
                <span>Recalculating combined route stops and feasibility metrics...</span>
              </div>
            }

            @let routeData = target.deliveryAmendment || target.attachmentPreview;
            @if (routeData && (target.status === 'PREVIEWED' || target.status === 'CONFIRMED' || target.status === 'APPLYING')) {
              <!-- Route Stops Table -->
              <div class="review-subpanel">
                <div class="subpanel-title">
                  <strong>Consolidated Route Sequence</strong>
                  <small>Pickup and drop-off precedence preserved</small>
                </div>
                <div class="route-table-wrapper">
                  <p-table [value]="routeData.route" [tableStyle]="{ 'min-width': '100%' }" styleClass="p-datatable-sm">
                    <ng-template pTemplate="header">
                      <tr>
                        <th style="width: 70px;">Stop #</th>
                        <th style="width: 100px;">Type</th>
                        <th>Location</th>
                        <th style="width: 130px;">Route Action</th>
                        <th style="width: 180px;">Planned Arrival</th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-stop>
                      <tr [class.row-added]="stop.status === 'ADDED'" [class.row-reused]="stop.status === 'REUSED'">
                        <td><strong>{{ stop.sequence }}</strong></td>
                        <td>
                          <span class="stop-type-badge" [class.stop-type--pickup]="stop.type === 'PICKUP'" [class.stop-type--dropoff]="stop.type === 'DROPOFF'">
                            {{ stop.type }}
                          </span>
                        </td>
                        <td>
                          {{ stop.locationName || ('Location #' + stop.locationId) }}
                          @if (stop.releaseId) {
                            <small class="stop-release-hint">(Release {{ stop.releaseId }})</small>
                          }
                        </td>
                        <td>
                          <p-tag [value]="stop.status" [severity]="stopStatusSeverity(stop.status)"></p-tag>
                        </td>
                        <td>{{ formatDate(stop.plannedArrival) }}</td>
                      </tr>
                    </ng-template>
                  </p-table>
                </div>
              </div>

              <!-- Impact Summary Grid -->
              <div class="review-subpanel">
                <div class="subpanel-title">
                  <strong>Estimated Operational Impact</strong>
                  <small>Combined route and capacity changes</small>
                </div>
                <div class="impact-metrics-grid">
                  <div class="metric-cell">
                    <span class="metric-label">Route Detour</span>
                    <strong class="metric-value">{{ formatDelta(routeData.impact.distanceDeltaKm, 'km') }}</strong>
                  </div>
                  <div class="metric-cell">
                    <span class="metric-label">Duration Change</span>
                    <strong class="metric-value">{{ formatMinutes(routeData.impact.durationDeltaMinutes) }}</strong>
                  </div>
                  <div class="metric-cell">
                    <span class="metric-label">Added Cargo Weight</span>
                    <strong class="metric-value">{{ formatDelta(routeData.impact.weightDeltaKg, 'kg') }}</strong>
                  </div>
                  <div class="metric-cell">
                    <span class="metric-label">Added Pallets</span>
                    <strong class="metric-value">{{ formatDelta(routeData.impact.palletsDelta, 'pallets') }}</strong>
                  </div>
                  <div class="metric-cell">
                    <span class="metric-label">Added Stops</span>
                    <strong class="metric-value">+{{ routeData.impact.addedStopsCount || 0 }}</strong>
                  </div>
                  <div class="metric-cell">
                    <span class="metric-label">Reused Stops</span>
                    <strong class="metric-value">{{ routeData.impact.reusedStopsCount || 0 }}</strong>
                  </div>
                </div>

                <!-- Waybill Notice -->
                @if (routeData.impact.waybillConsequence === 'WILL_CANCEL_AND_REISSUE') {
                  <div class="waybill-consequence-alert">
                    <i class="fa-solid fa-file-shield"></i>
                    <div>
                      <strong>Bayan Waybill Reissuance</strong>
                      <span>The existing electronic waybill will be cancelled and reissued to reflect the amended cargo and route stops upon confirmation.</span>
                    </div>
                  </div>
                }
              </div>

              <!-- Financials / Price Confirmation -->
              @if (target.deliveryId) {
                <div class="review-subpanel financials-panel">
                  <div class="subpanel-title">
                    <strong>Transport Financials & Reason</strong>
                    <small>Review and confirm revised carrier buying and customer selling prices</small>
                  </div>
                  <div class="financials-grid">
                    <div class="field-item">
                      <label for="buyingPrice-{{ target.targetKey }}">Confirmed Buying Price (SAR)</label>
                      <p-inputNumber
                        id="buyingPrice-{{ target.targetKey }}"
                        [(ngModel)]="target.confirmedBuyingPrice"
                        [min]="0"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="2"
                        [disabled]="target.status === 'CONFIRMED' || target.status === 'APPLYING'"></p-inputNumber>
                    </div>
                    <div class="field-item">
                      <label for="sellingPrice-{{ target.targetKey }}">Confirmed Selling Price (SAR)</label>
                      <p-inputNumber
                        id="sellingPrice-{{ target.targetKey }}"
                        [(ngModel)]="target.confirmedSellingPrice"
                        [min]="0"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="2"
                        [disabled]="target.status === 'CONFIRMED' || target.status === 'APPLYING'"></p-inputNumber>
                    </div>
                    <div class="field-item field-item--full">
                      <label for="reason-{{ target.targetKey }}">Amendment Reason <span class="required-star">*</span></label>
                      <input
                        pInputText
                        id="reason-{{ target.targetKey }}"
                        [(ngModel)]="target.amendmentReason"
                        placeholder="e.g. Added consolidated customer releases before dispatch"
                        [disabled]="target.status === 'CONFIRMED' || target.status === 'APPLYING'" />
                    </div>
                  </div>
                </div>
              } @else {
                <div class="no-delivery-price-note">
                  <i class="pi pi-info-circle"></i>
                  <span>Pricing remains unchanged for shipments without a delivery. Rates will be established when crew is assigned.</span>
                </div>
              }

              <!-- Independent Confirmation Action -->
              <div class="target-confirm-bar">
                @if (target.status === 'CONFIRMED') {
                  <div class="confirmed-success-badge">
                    <i class="pi pi-check-circle"></i>
                    <span>Confirmed! Releases successfully added to Shipment {{ target.shipmentNumber }}.</span>
                  </div>
                } @else {
                  <p-button
                    [label]="target.deliveryId ? 'Confirm Delivery Amendment' : 'Confirm Shipment Attachment'"
                    icon="pi pi-check"
                    severity="success"
                    [loading]="target.status === 'APPLYING'"
                    [disabled]="target.status === 'APPLYING'"
                    (onClick)="confirmTarget(target)"></p-button>
                }
              </div>
            }
          </div>
        }
      </div>

      <div class="step-bottom-actions">
        <p-button
          label="Back to Match Selection"
          icon="pi pi-arrow-left"
          severity="secondary"
          [outlined]="true"
          (onClick)="backToMatch()"></p-button>

        @if (hasAnyConfirmedTarget()) {
          <p-button
            label="View Confirmation Summary ({{ confirmedShipments.length }} confirmed)"
            icon="pi pi-arrow-right"
            iconPos="right"
            severity="success"
            (onClick)="goToConfirmation()"></p-button>
        }
      </div>
    </app-page-data-panel>
  }

  <!-- ==================== STEP 3: CONFIRMATION ==================== -->
  @if (step === 'CONFIRMATION') {
    <app-page-data-panel
      title="Shipment Attachment Summary"
      subtitle="The provisional releases have been confirmed into existing shipments. Each confirmed shipment is ready for execution.">

      <div class="confirmation-hero">
        <i class="pi pi-check-circle success-hero-icon"></i>
        <h2>{{ confirmedShipments.length }} Shipment{{ confirmedShipments.length === 1 ? '' : 's' }} Successfully Updated</h2>
        <p>Selected order releases have been incorporated into shipments with merged routes, updated payload details, and execution links.</p>
      </div>

      <!-- Truck Plan Style Links for Confirmed Shipments -->
      <div class="confirmed-cards-list">
        @for (confirmed of confirmedShipments; track confirmed.shipmentId) {
          <div class="confirmed-card">
            <div class="confirmed-card-head">
              <div class="confirmed-title">
                <strong>Shipment {{ confirmed.shipmentNumber }}</strong>
                @if (confirmed.deliveryNumber) {
                  <span class="delivery-badge">Delivery {{ confirmed.deliveryNumber }}</span>
                }
              </div>
              <p-tag severity="success" value="Confirmed & Linked"></p-tag>
            </div>

            <div class="confirmed-releases-list">
              <span class="releases-label">Incorporated Releases:</span>
              @for (relNum of confirmed.releaseNumbers; track relNum) {
                <span class="release-pill">{{ relNum }}</span>
              }
            </div>

            <!-- Styled exactly in the truck plan view style -->
            <div class="state-shipment-links" aria-label="Confirmed shipment links">
              <a [routerLink]="['/shipment/view', confirmed.shipmentId]">
                <i class="pi pi-truck"></i>
                <span>Open Shipment {{ confirmed.shipmentNumber }}</span>
                <i class="pi pi-arrow-right"></i>
              </a>
            </div>
          </div>
        }
      </div>

      <!-- Keep Failed Targets Available for Correction -->
      @if (hasFailedTargets()) {
        <div class="unconfirmed-section">
          <div class="unconfirmed-title">
            <i class="pi pi-exclamation-triangle"></i>
            <strong>Unconfirmed Shipment Groups</strong>
            <small>The following provisional groups were not confirmed. They remain available for review and correction.</small>
          </div>
          @for (target of targetPreviews; track target.targetKey) {
            @if (target.status !== 'CONFIRMED') {
              <div class="unconfirmed-card">
                <div>
                  <strong>Shipment {{ target.shipmentNumber }}</strong>
                  @if (target.errorMessage) {
                    <div class="err-text">{{ target.errorMessage }}</div>
                  }
                </div>
                <p-button
                  label="Review & Fix"
                  icon="pi pi-arrow-left"
                  size="small"
                  severity="warn"
                  [outlined]="true"
                  (onClick)="step = 'REVIEW'"></p-button>
              </div>
            }
          }
        </div>
      }

      <div class="step-bottom-actions">
        <p-button
          label="Return to Order Releases"
          icon="pi pi-arrow-left"
          (onClick)="returnToReleaseList()"></p-button>
      </div>
    </app-page-data-panel>
  }
</app-page-shell>
